import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { Destination, Location } from "../../assets/icons";
import "swiper/css";
import "swiper/css/pagination";
import "../../css/Home/listItem.css"; // 스타일 파일 import
import SaveButton from "./Button/SaveButton";
import defaultCruiseImage from "../../assets/img/detail-dummy/dummy-cruise.jpg";

const RouteField = ({ icon: Icon, value, className = "" }) => {
  const textRef = useRef(null);
  const containerRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [travelDuration, setTravelDuration] = useState(6);
  const [animationKey, setAnimationKey] = useState(0);
  const holdTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const containerEl = containerRef.current;
      const textEl = textRef.current;
      if (!containerEl || !textEl) return;
      const distance = textEl.scrollWidth - containerEl.clientWidth;
      if (distance > 0) {
        setIsOverflow(true);
        setScrollDistance(distance);
        const duration = Math.min(Math.max(distance / 25, 4), 12);
        setTravelDuration(duration);
      } else {
        setIsOverflow(false);
        setScrollDistance(0);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
    };
  }, [value]);

  useEffect(() => {
    if (!containerRef.current) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    observerRef.current.observe(containerRef.current);
    return () => {
      if (observerRef.current && containerRef.current) {
        observerRef.current.unobserve(containerRef.current);
      }
    };
  }, []);

  const shouldAnimate = isOverflow && isVisible;

  useEffect(() => {
    if (shouldAnimate) {
      setAnimationKey(prev => prev + 1);
    } else {
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
      setAnimationKey(0);
    }
    return () => {
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
    };
  }, [shouldAnimate, value]);

  const handleAnimationEnd = () => {
    if (!shouldAnimate) return;
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
    holdTimeoutRef.current = setTimeout(() => {
      setAnimationKey(prev => prev + 1);
    }, 1500);
  };

  if (!value) return null;

  return (
    <div className={`route-field ${className}`.trim()}>
      <Icon />
      <div
        ref={containerRef}
        className={`route-text-wrapper${shouldAnimate ? " is-overflow" : ""}`}
        style={
          shouldAnimate
            ? {
              "--scroll-distance": `${scrollDistance}px`,
              "--marquee-duration": `${travelDuration}s`,
            }
            : undefined
        }
      >
        <span
          key={animationKey}
          ref={textRef}
          className="route-text"
          onAnimationEnd={handleAnimationEnd}
          style={
            shouldAnimate
              ? {
                "--marquee-duration": `${travelDuration}s`,
              }
              : undefined
          }
        >
          {value}
        </span>
      </div>
    </div>
  );
};

// 홈/검색 목록에서 개별 크루즈 상품 카드를 렌더링한다.
export default function ListItem({ item }) {
  const navigate = useNavigate();
  const images = Array.isArray(item.images) && item.images.length > 0
    ? item.images
    : [defaultCruiseImage];

  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = defaultCruiseImage;
  };

  return (
    <div className="list-item-wrapper">
      <div className="list-item" onClick={() => navigate(`/detail/${item.id}`)}
        style={{position:'relative'}}
      >
        {/* 🔹 썸네일 이미지 Swiper */}
        <Swiper
          pagination={{ clickable: true }} // 네비게이션 점 추가
          modules={[Pagination]}
          className="image-swiper"
          nested={true}          // 중첩 Swiper 설정 추가
          touchStartPropagation={false}  // 부모 이벤트 전파 방지 추가
          resistanceRatio={0}     // 🔹 이거 추가
        >
          {images.map((image, index) => (
            <SwiperSlide key={index} className="swiper-slide">
              <img
                src={image || defaultCruiseImage}
                alt={`Thumbnail ${index}`}
                className="swiper-image"
                onError={handleImageError}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 🔹 텍스트 정보 */}
        <div className="item-details">
          <p className="route">
            <RouteField icon={Location} value={item.departure} className="departure" />
            <div className="arrow-container">
              <Destination className="arrow-icon" />
            </div>
            <RouteField icon={Location} value={item.destination} className="destination" />
          </p>
          <p className="schedule-info">
            {item.startDate} | <span>{item.duration}</span>
          </p>
          <p className="ship-info">
            {item.ship}
            {item.ton ? ` | ${item.ton}` : ''}
          </p>
          <p className="price">
            {
              !isNaN(Number(item.price))
                ? `${Number((item.price / 2)).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}~`
                : `${(item.price / 2).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}~`
            }
          </p>
        </div>

      </div>
      <SaveButton
        size="L"
        className="list-item-save-btn"
        id={Array.isArray(item.id) ? item.id[0] : item.id}
      />
    </div>
  );
}
